import React from 'react';
import * as Popover from '@radix-ui/react-popover';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Settings, ChevronDown, X, Check, Lock } from 'lucide-react';
import clearOnEscape, { escapeWouldClearField } from './clearOnEscape';
import './PageSettingsPopover.css';

/**
 * Baustein — Page-Settings (Gear-Popover).
 * Trennt Filter (verengt Sicht) von Settings (verändert Verhalten).
 *
 * Komposition (alle Sub-Components optional):
 *   <PageSettingsPopover hasChanges>
 *     <PageSettingsPopover.Section label="Verfügbarkeit">
 *       <PageSettingsPopover.Item
 *         checked
 *         label="Karten aus anderen Decks zählen"
 *         description="Bestimmt, ob virtuelle Decks auch zu „verfügbar" beitragen."
 *       >
 *         <PageSettingsPopover.PickerMenu options={[{ value, label, locked? }]} value={ids} onChange={setIds} />
 *       </PageSettingsPopover.Item>
 *     </PageSettingsPopover.Section>
 *     <PageSettingsPopover.Section label="Print-Wahl">
 *       <PageSettingsPopover.Radio
 *         value="preferred"
 *         onChange={...}
 *         options={[
 *           { value: 'preferred', label: 'Bevorzugter Druck' },
 *           { value: 'any', label: 'Beliebiger Druck' },
 *         ]}
 *       />
 *     </PageSettingsPopover.Section>
 *   </PageSettingsPopover>
 */
export default function PageSettingsPopover({ children, hasChanges = false, label = 'Seiten-Einstellungen' }) {
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button type="button" className="btn-icon page-settings-trigger" aria-label={label}>
          <Settings size={16} aria-hidden />
          {hasChanges && <span className="page-settings-trigger__indicator" aria-hidden />}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          asChild
          sideOffset={6}
          align="end"
          onEscapeKeyDown={(event) => {
            if (escapeWouldClearField()) event.preventDefault();
          }}
        >
          <div className="page-settings-popover">{children}</div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** Section-Header innerhalb des Popovers — gruppiert verwandte Settings */
PageSettingsPopover.Section = function PageSettingsSection({ label, children }) {
  return (
    <div className="page-settings-popover__section">
      {label && <div className="page-settings-popover__section-label">{label}</div>}
      <div className="page-settings-popover__section-items">{children}</div>
    </div>
  );
};

/**
 * Boolean-Setting (Checkbox + Label + optional Description + optional abhängige Children).
 * Children werden eingerückt und nur sichtbar, wenn `checked` true ist.
 *
 * `disabled` friert die Checkbox ein (z.B. Upgrade-Modus: Quell-Decks erzwungen,
 * oder Export-Option ohne passende Karten). Wichtig: disabled dimmt NUR die
 * Checkbox-Zeile — bei `checked && disabled` bleiben die Children sichtbar und
 * voll interaktiv (der Deck-Picker muss im Upgrade-Modus bedienbar bleiben).
 */
PageSettingsPopover.Item = function PageSettingsItem({
  checked = false,
  onChange,
  label,
  description,
  disabled = false,
  children,
}) {
  return (
    <div className="page-settings-popover__item">
      <label className={`page-settings-popover__item-row${disabled ? ' page-settings-popover__item-row--disabled' : ''}`}>
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => !disabled && onChange?.(e.target.checked)}
        />
        <span className="page-settings-popover__item-label">{label}</span>
      </label>
      {description && (
        <p className="page-settings-popover__item-description">{description}</p>
      )}
      {checked && children && (
        <div className="page-settings-popover__item-children">{children}</div>
      )}
    </div>
  );
};

/**
 * PickerMenu — Picker-Trigger („3 Decks ausgewählt ▾") + Mehrfachauswahl-Menü
 * (Radix dropdown-menu), dieselbe Menü-Mechanik wie `FilterStrip.PillMenu`
 * (mode='multi'), aber in der Settings-Optik. DAS Pattern für
 * Auswahl-Settings (z.B. „welche Decks zählen als Quelle").
 *
 * Gesperrte Einträge (`locked: true`) sind immer ausgewählt und nicht
 * abwählbar — sie zeigen ein Schloss statt Checkbox-Platz (z.B. Quell-Decks,
 * die der Upgrade-Modus automatisch festgesetzt hat).
 *
 * Props:
 *   options     — [{ value, label, locked? }]
 *   value       — string[] (ausgewählte values; gesperrte gelten immer als ausgewählt)
 *   onChange    — (nextArray: string[]) => void — enthält gesperrte values immer
 *   placeholder — Trigger-Text bei 0 Auswahl (default 'Auswählen…')
 *
 * Wichtig: Das Menü rendert OHNE Portal, damit es im DOM-Baum des
 * umgebenden Settings-Popovers bleibt — sonst wertet der Radix-Popover den
 * Klick ins (portalte) Menü als „außerhalb" und schließt sich.
 */
PageSettingsPopover.PickerMenu = function PageSettingsPickerMenu({
  options = [],
  value = [],
  onChange,
  placeholder = 'Auswählen…',
}) {
  const lockedValues = options.filter((o) => o.locked).map((o) => o.value);
  const selected = [...new Set([...(value || []), ...lockedValues])];

  const toggle = (optValue) => {
    if (lockedValues.includes(optValue)) return;
    const next = selected.includes(optValue)
      ? selected.filter((v) => v !== optValue)
      : [...selected, optValue];
    onChange?.(next);
  };

  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>
        <button type="button" className="page-settings-popover__picker">
          <span>
            {selected.length > 0 ? `${selected.length} ausgewählt` : placeholder}
          </span>
          <ChevronDown size={14} aria-hidden />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        className="page-settings-popover__menu"
        sideOffset={4}
        align="start"
      >
        {options.map((opt) => (
          <DropdownMenu.CheckboxItem
            key={opt.value}
            className={`page-settings-popover__menu-item${opt.locked ? ' page-settings-popover__menu-item--locked' : ''}`}
            checked={selected.includes(opt.value)}
            disabled={!!opt.locked}
            onCheckedChange={() => toggle(opt.value)}
            onSelect={(e) => e.preventDefault()}
          >
            <span className="page-settings-popover__menu-check">
              {opt.locked ? (
                <Lock size={12} aria-label="Festgesetzt" />
              ) : (
                <DropdownMenu.ItemIndicator>
                  <Check size={14} aria-hidden />
                </DropdownMenu.ItemIndicator>
              )}
            </span>
            {opt.label}
          </DropdownMenu.CheckboxItem>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

/**
 * Radio-Gruppe für Mutually-Exclusive-Optionen.
 * Options: [{ value, label, description? }]
 */
PageSettingsPopover.Radio = function PageSettingsRadio({ name = 'page-settings-radio', value, onChange, options = [] }) {
  return (
    <div className="page-settings-popover__radio-group">
      {options.map((opt) => (
        <label key={opt.value} className="page-settings-popover__radio-row">
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange?.(opt.value)}
          />
          <span>
            <span className="page-settings-popover__item-label">{opt.label}</span>
            {opt.description && (
              <p className="page-settings-popover__item-description">{opt.description}</p>
            )}
          </span>
        </label>
      ))}
    </div>
  );
};

/**
 * TextInput — Setting mit Label links und Text-Eingabefeld rechts.
 * Beispiel: Postleitzahl, Distanz-Limit, …
 *
 * Props:
 *   label        — Label-Text (links)
 *   value        — kontrollierter Eingabewert
 *   onChange     — (value: string) => void
 *   placeholder  — optional
 *   width        — CSS-Breite des Inputs (z.B. '80px'), default '120px'
 *   inputProps   — zusätzliche Props ans <input> (type, maxLength, …)
 *   error        — optionaler Fehlertext; zeigt rote Border + Meldung unter dem Feld
 *   onClear      — optionaler Callback; zeigt ein ✕ zum Leeren, sobald `value` nicht leer ist.
 *                  Escape leert dann ebenfalls und lässt den Popover offen; ein zweites
 *                  Escape schließt ihn.
 */
PageSettingsPopover.TextInput = function PageSettingsTextInput({
  label,
  value,
  onChange,
  placeholder,
  width = '120px',
  inputProps = {},
  error,
  onClear,
}) {
  const showClear = typeof onClear === 'function' && value;
  const { onKeyDown: inputOnKeyDown, ...restInputProps } = inputProps;
  return (
    <div className="page-settings-popover__field">
      <label className="page-settings-popover__field-row">
        <span className="page-settings-popover__item-label">{label}</span>
        <span className="page-settings-popover__field-control" style={{ width }}>
          <input
            type="text"
            className={`page-settings-popover__field-input${error ? ' page-settings-popover__field-input--error' : ''}`}
            value={value ?? ''}
            onChange={(e) => onChange?.(e.target.value)}
            placeholder={placeholder}
            {...restInputProps}
            {...clearOnEscape({
              hasValue: Boolean(showClear),
              clear: () => onClear?.(),
              onKeyDown: inputOnKeyDown,
            })}
          />
          {showClear && (
            <button
              type="button"
              className="page-settings-popover__field-clear"
              onClick={onClear}
              aria-label="Eingabe entfernen"
            >
              <X size={14} aria-hidden />
            </button>
          )}
        </span>
      </label>
      {error && <p className="page-settings-popover__field-error">{error}</p>}
    </div>
  );
};

/**
 * Toggle — Boolean-Setting mit Switch-Optik statt Checkbox.
 * Layout-Spiegelbild der Item-Row, aber visuell als „aus / an"-Schalter.
 * Verwende statt `Item`, wenn das Setting ein klares on/off ist (z.B.
 * „Auffindbar", „Sichtbar für Freunde"), nicht eine Auswahl aus Optionen.
 *
 * Props:
 *   checked      — kontrolliertes Boolean
 *   onChange     — (value: boolean) => void
 *   label        — Label-Text (links)
 *   description  — optionaler Erklärtext unter dem Label
 *   disabled     — wenn true, ist der Schalter ausgegraut und nicht umschaltbar
 *                  (z.B. solange eine Vorbedingung fehlt); der Grund gehört in `description`.
 */
PageSettingsPopover.Toggle = function PageSettingsToggle({
  checked = false,
  onChange,
  label,
  description,
  disabled = false,
}) {
  return (
    <div className={`page-settings-popover__item${disabled ? ' page-settings-popover__item--disabled' : ''}`}>
      <label className="page-settings-popover__toggle-row">
        <span className="page-settings-popover__item-label">{label}</span>
        <span className="page-settings-popover__toggle">
          <input
            type="checkbox"
            className="page-settings-popover__toggle-input"
            checked={checked}
            disabled={disabled}
            onChange={(e) => onChange?.(e.target.checked)}
          />
          <span className="page-settings-popover__toggle-track" aria-hidden>
            <span className="page-settings-popover__toggle-knob" />
          </span>
        </span>
      </label>
      {description && (
        <p className="page-settings-popover__toggle-description">{description}</p>
      )}
    </div>
  );
};

/**
 * Select — Setting mit Label links und Dropdown rechts.
 * Beispiel: Suchradius, Standard-Print-Quelle, …
 *
 * Props:
 *   label    — Label-Text (links)
 *   value    — aktueller Wert
 *   onChange — (value: string) => void
 *   options  — [{ value, label }]
 */
PageSettingsPopover.Select = function PageSettingsSelect({ label, value, onChange, options = [] }) {
  return (
    <label className="page-settings-popover__field-row">
      <span className="page-settings-popover__item-label">{label}</span>
      <select
        className="page-settings-popover__field-input"
        value={value ?? ''}
        onChange={(e) => onChange?.(e.target.value)}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </label>
  );
};
